import React, { useState } from "react";
import {
  getNicknameFromId,
  getAnimalFromId,
  getAnimalEmoji,
} from "./Avatar.jsx";

// Read-only collapsible panel showing the two static synthetic peers' estimates
// and rationales, matching the panel FinalVote renders. The convincingness
// ratings the participant gave during peer review are deliberately not shown.
export function PeerRationalesPanel({
  peers = [],
  formatBelief = (value) => String(value ?? "").trim(),
  subtitle = "Reopen the two other participants' responses while making your final estimate.",
  defaultOpen = false,
}) {
  const [showPeerRationales, setShowPeerRationales] = useState(defaultOpen);

  if (!Array.isArray(peers) || peers.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900">
            Other Participants' Responses
          </h3>
          <p className="mt-1 text-sm text-gray-600">{subtitle}</p>
        </div>
        <button
          type="button"
          className="shrink-0 text-sm px-3 py-2 rounded-md font-semibold shadow-sm border border-empirica-500 text-white bg-empirica-600 hover:bg-empirica-700 focus:outline-none focus:ring-2 focus:ring-empirica-300"
          onClick={() => setShowPeerRationales((prev) => !prev)}
        >
          {showPeerRationales ? "Hide responses" : "Show responses"}
        </button>
      </div>

      {showPeerRationales && (
        <div className="mt-5 space-y-4">
          {peers.map((groupMember) => {
            const memberId = groupMember.id;
            const nickname = groupMember?.name || getNicknameFromId(memberId);
            const animal = groupMember?.assignedAnimal || getAnimalFromId(memberId);
            const emoji = getAnimalEmoji(animal);
            const rationaleText =
              String(groupMember?.rationaleText || "").trim() ||
              "This earlier participant response is unavailable.";
            const estimate = groupMember?.initialBelief;
            const hasEstimate =
              estimate !== undefined &&
              estimate !== null &&
              String(estimate).trim() !== "";

            return (
              <div
                key={memberId}
                className="rounded-lg border border-gray-200 bg-gray-50 p-4"
              >
                <div className="mb-3 flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-white shadow flex items-center justify-center text-2xl">
                    <span aria-hidden>{emoji}</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{nickname}</div>
                    {hasEstimate && (
                      <div className="text-sm text-gray-600">
                        Estimate:{" "}
                        <span className="font-medium">
                          {formatBelief(estimate)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <p className="whitespace-pre-wrap text-sm leading-6 text-gray-800">
                  {rationaleText}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Resolves the stored static synthetic peers from whichever scope holds them.
export function useStaticSyntheticPeers({ game, round, stage } = {}) {
  const raw =
    stage?.get?.("staticSyntheticPeers") ||
    round?.get?.("staticSyntheticPeers") ||
    game?.get?.("staticSyntheticPeers") ||
    [];
  return Array.isArray(raw) ? raw : [];
}
